import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { MSAL_INSTANCE } from '@azure/msal-angular';
import { AccountInfo, IPublicClientApplication, SilentRequest } from '@azure/msal-browser';
import { AppInitService } from '../services/app-init.service';
import { catchError, defer, finalize, switchMap, throwError } from 'rxjs';
import { SpinnerService } from '../services/spinner.service';
import { SKIP_API_TOKEN_REFRESH } from '../services/control-systems.service';
import { environment } from '../../../../../environments/environment';

export const cSInterceptorInterceptor: HttpInterceptorFn = (req, next) => {
  const msalInstance = inject<IPublicClientApplication>(MSAL_INSTANCE);
  const appInitService = inject(AppInitService);
  const spinnerService = inject(SpinnerService);

  const matchesApi = (baseUrl?: string): boolean => {
    if (!baseUrl) return false;
    try {
      const requestUrl = new URL(req.url, window.location.origin);
      const apiUrl = new URL(baseUrl, window.location.origin);
      const basePath = apiUrl.pathname.replace(/\/+$/, '');
      return requestUrl.origin === apiUrl.origin &&
        (!basePath || requestUrl.pathname === basePath || requestUrl.pathname.startsWith(`${basePath}/`));
    } catch {
      return false;
    }
  };

  const authenticate = environment.name.includes('cloud') && req.method !== 'OPTIONS';
  const isEngTool = authenticate && matchesApi(environment.engtoolapi);
  const isEngHub = authenticate && !isEngTool && matchesApi(environment.enghubapi);

  const authenticatedRequest = () => {
    if (isEngTool) {
      const microsoftAccessToken = sessionStorage.getItem('microsoftAccessToken');
      return req.clone({
        setHeaders: {
          Accept: 'application/json',
          ...(microsoftAccessToken ? { Authorization: `Bearer ${microsoftAccessToken}` } : {}),
          ...(req.body instanceof FormData || req.headers.has('Content-Type')
            ? {} : { 'Content-Type': 'application/json; charset=utf-8' })
        }
      });
    }
    if (isEngHub) {
      const controlSystemsAccessToken = sessionStorage.getItem('controlSystemsAccessToken');
      return req.clone({
        setHeaders: {
          Accept: '*/*',
          ...(controlSystemsAccessToken ? { Authorization: `Bearer ${controlSystemsAccessToken}` } : {}),
          'X-Resource-Id': sessionStorage.getItem('resource') || '',
          'X-Slot-Id': sessionStorage.getItem('slot') || ''
        }
      });
    }
    return req;
  };

  spinnerService.show();
  return next(authenticatedRequest()).pipe(
    catchError((error: unknown) => {
      if ((!isEngTool && !isEngHub) || req.context.get(SKIP_API_TOKEN_REFRESH) ||
        !(error instanceof HttpErrorResponse) || error.status !== 401) {
        return throwError(() => error);
      }

      const storedAccount = sessionStorage.getItem('account');
      if (!storedAccount) return throwError(() => error);

      let account: AccountInfo;
      try {
        account = JSON.parse(decodeURIComponent(storedAccount)) as AccountInfo;
      } catch {
        return throwError(() => error);
      }
      if (!account || !environment.apiScopes.length) return throwError(() => error);

      const silentRequest: SilentRequest = {
        account,
        scopes: [...environment.apiScopes],
        forceRefresh: true
      };

      return defer(() => msalInstance.acquireTokenSilent(silentRequest)).pipe(
        switchMap(result => {
          if (!result.accessToken) return throwError(() => error);
          sessionStorage.setItem('microsoftAccessToken', result.accessToken);
          if (result.account?.username) sessionStorage.setItem('username', result.account.username);
          return appInitService.postControlSystemsToken$();
        }),
        switchMap(() => next(authenticatedRequest()))
      );
    }),
    finalize(() => spinnerService.hide())
  );
};